/**
 * Work Management Resource
 *
 * Thin 1:1 mapping to the work management API. No business logic, no default
 * parameter injection. The SDK passes params as-is.
 */

import type { HttpLayer } from '../http.js';
import type {
  Team,
  CreateTeamRequest,
  UpdateTeamRequest,
  Goal,
  GoalRoadmap,
  GoalRestartResult,
  GoalReconcileResult,
  GoalContinueResult,
  GoalActivity,
  SessionWorkItem,
  WorkItemFilesResponse,
  FileComparisonResponse,
  CompareFileVersionsParams,
  WorkItemPathEntry,
  ListPathWorkItemsParams,
  RebuildFileProvenanceResult,
  CreateGoalRequest,
  ListGoalsParams,
  SetGoalScheduleRequest,
  WorkOrdering,
  AddWorkOrderingRequest,
  Ticket,
  CreateTicketRequest,
  UpdateTicketRequest,
  ListTicketsParams,
  TicketListResponse,
  TicketTransitionRequest,
  AssignTicketRequest,
  Comment,
  AddCommentRequest,
  ListCommentsParams,
  AddDependencyRequest,
  WorkItemKind,
  StartWorkSessionRequest,
  StartWorkSessionResponse,
  Project,
  ProjectDetail,
  CreateProjectRequest,
  UpdateProjectRequest,
  AssignProjectRequest,
  SetProjectVerificationRequest,
  Initiative,
  CreateInitiativeRequest,
  UpdateInitiativeRequest,
  LinkProjectRequest,
  BoardViewResponse,
  SessionTicketMapping,
  TicketSession,
  TicketDetail,
  Position,
  ExecutionCadence,
} from '../types/index.js';
import type { RebuildFileProvenanceParams } from '../types/work-management.js';

export class WorkManagementResource {
  constructor(private http: HttpLayer) {}

  private prefix = '/v1/work';

  // --- Teams ---

  /**
   * Create a team — omit workspace_id to provision a new cloud workspace, or pass workspace_id to bind an existing active workspace (local or cloud) not already owned by another team.
   * Does not create or staff a manager. A later {@link WorkManagementResource.createGoal} 422s until
   * {@link OrgChartResource.staffPosition} occupies a `manager` seat on this
   * team (persona + agent process). Non-CEO seats need a `parent_id`.
   */
  async createTeam(params: CreateTeamRequest): Promise<Team> {
    return this.http.post(`${this.prefix}/teams`, params);
  }

  async listTeams(): Promise<Team[]> {
    return this.http.get(`${this.prefix}/teams`);
  }

  async getTeam(teamId: string): Promise<Team> {
    return this.http.get(`${this.prefix}/teams/${teamId}`);
  }

  async updateTeam(teamId: string, params: UpdateTeamRequest): Promise<Team> {
    return this.http.put(`${this.prefix}/teams/${teamId}`, params);
  }

  async deleteTeam(teamId: string): Promise<void> {
    return this.http.delete(`${this.prefix}/teams/${teamId}`);
  }

  /**
   * List the org-chart positions assigned to a team — the team's roster.
   *
   * A team's membership IS its slice of the org chart: a position carries the
   * `team_id` and the occupant (`user_id` or `persona_id`), so there is no
   * separate member record to read. Returns every position whose `team_id` is
   * `teamId`, occupied or vacant.
   */
  async listTeamRoster(teamId: string): Promise<Position[]> {
    return this.http.get(`${this.prefix}/teams/${teamId}/roster`);
  }

  // --- Goals ---

  /**
   * Create a goal for a team to execute.
   *
   * `schedule_kind` decides when the team picks it up: `immediate` starts
   * decomposition now, `at` requires `schedule_at` (RFC3339), and `recurring`
   * requires `schedule_cron`. The goal is returned in its initial state; the
   * manager session and automation attach asynchronously.
   *
   * The team's manager position must already be staffed (`persona_id` and
   * `agent_process_id`). A vacant manager is a 422 naming
   * {@link OrgChartResource.staffPosition}.
   */
  async createGoal(params: CreateGoalRequest): Promise<Goal> {
    return this.http.post(`${this.prefix}/goals`, params);
  }

  /**
   * List goals, optionally narrowed to one team and/or one state.
   */
  async listGoals(params?: ListGoalsParams): Promise<Goal[]> {
    return this.http.get(`${this.prefix}/goals`, params);
  }

  /**
   * Read one goal. 404s when no goal with that id exists in the caller's tenant.
   */
  async getGoal(goalId: string): Promise<Goal> {
    return this.http.get(`${this.prefix}/goals/${goalId}`);
  }

  /**
   * Replace a goal's schedule, returning the updated goal.
   *
   * The same `schedule_kind` rules as {@link WorkManagementResource.createGoal}
   * apply: `at` requires `schedule_at`, `recurring` requires `schedule_cron`.
   */
  async setGoalSchedule(goalId: string, params: SetGoalScheduleRequest): Promise<Goal> {
    return this.http.put(`${this.prefix}/goals/${goalId}/schedule`, params);
  }

  /**
   * Cancel a goal, returning it in the `cancelled` state. Terminal — a cancelled
   * goal is not rescheduled, and its work stops being advanced.
   */
  async cancelGoal(goalId: string): Promise<Goal> {
    return this.http.post(`${this.prefix}/goals/${goalId}/cancel`);
  }

  /**
   * Restart a goal: cancel it, stop everything it has running, and create a NEW
   * goal with the same objective, team, schedule and creator.
   *
   * Returns the {@link GoalRestartResult} envelope, NOT a flat `Goal` — the new
   * goal is under `goal`, and `previous_goal_id` names the run it replaces.
   * Nothing is deleted: the prior goal, its initiatives and its projects stay
   * readable, which is what makes one run comparable against the next. Its
   * unfinished projects and tickets are moved to `cancelled`, so the prior run
   * stops being advanced.
   *
   * This is how a prompt is iterated on — a product action, not a database edit.
   * 404s when no goal with that id exists in the caller's tenant.
   */
  async restartGoal(goalId: string): Promise<GoalRestartResult> {
    return this.http.post(`${this.prefix}/goals/${goalId}/restart`);
  }

  /**
   * Re-derive a goal's label from the actual state of its projects and write
   * it if it has fallen behind, returning the reconcile result.
   *
   * The label may only advance (`scheduled`/`decomposing` → `executing`/
   * `verifying`) — it never moves backwards. A goal with no projects, a
   * `draft` goal, or a goal already settled (`completed`/`cancelled`/`failed`)
   * or `paused` is returned unchanged with a `reason` naming why nothing
   * moved. When every project is settled, the completion rollup is re-driven,
   * which is what lets a stalled goal finish.
   *
   * This is the recovery lever when the label no longer describes the work —
   * the state is derived from the census, never asserted.
   * 404s when no goal with that id exists in the caller's tenant.
   */
  async reconcileGoalState(goalId: string): Promise<GoalReconcileResult> {
    return this.http.post(`${this.prefix}/goals/${goalId}/reconcile-state`);
  }

  /**
   * Re-kick a stalled but active goal: reconcile its label, clear the plan
   * cycles that stopped its non-terminal projects, restart each one's run,
   * and fire the schedule sweep so due triggers run now.
   *
   * This is the recovery lever when the team is not working the tickets — the
   * exhausted-bound case nothing else un-sticks. A settled goal is returned
   * with `status: "skipped"` and nothing runs. Idempotent: safe to call again
   * after a partial recovery.
   */
  async continueGoal(goalId: string): Promise<GoalContinueResult> {
    return this.http.post(`${this.prefix}/goals/${goalId}/continue`);
  }

  /**
   * Read a goal together with its ordering edges — the goal's roadmap.
   *
   * Returns the {@link GoalRoadmap} envelope (`{ goal, orderings }`), NOT a flat
   * goal: one round trip for the observation surface that renders a goal and the
   * dependency edges between its work items.
   */
  async getGoalRoadmap(goalId: string): Promise<GoalRoadmap> {
    return this.http.get(`${this.prefix}/goals/${goalId}/roadmap`);
  }

  /**
   * Read one goal together with everything happening beneath it — its
   * initiatives, their projects, and those projects' tasks — as ONE assembled
   * snapshot.
   *
   * Returns the {@link GoalActivity} envelope (`{ goal, initiatives }`), NOT a
   * flat `Goal`. Every level carries its state, its assignee, and the id of the
   * session working it when one exists, so the observation surface renders the
   * hierarchy without composing four reads client-side — four reads can
   * interleave with a process advance and show a state that never existed.
   */
  async getGoalActivity(goalId: string): Promise<GoalActivity> {
    return this.http.get(`${this.prefix}/goals/${goalId}/activity`);
  }

  // --- Work Ordering ---

  /**
   * Add an ordering edge between two of a goal's work items: `blocked_node_id`
   * waits for `blocking_node_id`.
   *
   * Both endpoints must be nodes of the SAME goal and of the kind named by
   * `node_kind` (`initiative` or `project`); a self-edge or a foreign endpoint is
   * rejected.
   */
  async addWorkOrdering(goalId: string, params: AddWorkOrderingRequest): Promise<WorkOrdering> {
    return this.http.post(`${this.prefix}/goals/${goalId}/orderings`, params);
  }

  /**
   * List a goal's ordering edges.
   */
  async listWorkOrderings(goalId: string): Promise<WorkOrdering[]> {
    return this.http.get(`${this.prefix}/goals/${goalId}/orderings`);
  }

  /**
   * Remove one ordering edge, unblocking whatever it was gating.
   */
  async removeWorkOrdering(goalId: string, orderingId: string): Promise<void> {
    return this.http.delete(`${this.prefix}/goals/${goalId}/orderings/${orderingId}`);
  }

  // --- Tickets ---

  async createTicket(params: CreateTicketRequest): Promise<Ticket> {
    return this.http.post(`${this.prefix}/tickets`, params);
  }

  async listTickets(params?: ListTicketsParams): Promise<TicketListResponse> {
    return this.http.get(`${this.prefix}/tickets`, params);
  }

  async getTicket(ticketId: string): Promise<TicketDetail> {
    return this.http.get(`${this.prefix}/tickets/${ticketId}`);
  }

  async updateTicket(ticketId: string, params: UpdateTicketRequest): Promise<Ticket> {
    return this.http.put(`${this.prefix}/tickets/${ticketId}`, params);
  }

  async deleteTicket(ticketId: string): Promise<void> {
    return this.http.delete(`${this.prefix}/tickets/${ticketId}`);
  }

  async transitionTicketState(ticketId: string, params: TicketTransitionRequest): Promise<Ticket> {
    return this.http.post(`${this.prefix}/tickets/${ticketId}/transition`, params);
  }

  async assignTicket(ticketId: string, params: AssignTicketRequest): Promise<Ticket> {
    return this.http.post(`${this.prefix}/tickets/${ticketId}/assign`, params);
  }

  // --- Comments ---

  async addComment(ticketId: string, params: AddCommentRequest): Promise<Comment> {
    return this.http.post(`${this.prefix}/tickets/${ticketId}/comments`, params);
  }

  async listComments(ticketId: string, params?: ListCommentsParams): Promise<Comment[]> {
    return this.http.get(`${this.prefix}/tickets/${ticketId}/comments`, params);
  }

  // --- Activity ---

  async listTicketActivity(
    ticketId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<unknown[]> {
    return this.http.get(`${this.prefix}/tickets/${ticketId}/activity`, params);
  }

  // --- Dependencies ---

  async addDependency(ticketId: string, params: AddDependencyRequest): Promise<unknown> {
    return this.http.post(`${this.prefix}/tickets/${ticketId}/dependencies`, params);
  }

  async removeDependency(ticketId: string, depId: string): Promise<void> {
    return this.http.delete(`${this.prefix}/tickets/${ticketId}/dependencies/${depId}`);
  }

  // --- Work Sessions ---

  /**
   * Start a work session on a work item and ignite its first turn. The
   * executing role is the work item's own — for a ticket it resolves through
   * the ticket's project to that project's assigned position, so
   * `persona_id`, `agent_process_id` and `workspace_id` are omitted unless you
   * are deliberately overriding the role.
   *
   * @param workItemKind - `goal`, `project` or `ticket`.
   * @param workItemId - The work item to start work on.
   * @param params - The opening prompt, and any role override.
   * @returns The started session id.
   */
  async startWorkSession(
    workItemKind: WorkItemKind,
    workItemId: string,
    params: StartWorkSessionRequest
  ): Promise<StartWorkSessionResponse> {
    return this.http.post(
      `${this.prefix}/work-items/${workItemKind}/${workItemId}/sessions`,
      params
    );
  }

  // --- Ticket Sessions ---

  async listTicketSessions(ticketId: string): Promise<TicketSession[]> {
    return this.http.get(`${this.prefix}/tickets/${ticketId}/sessions`);
  }

  // --- Projects ---

  async createProject(params: CreateProjectRequest): Promise<Project> {
    return this.http.post(`${this.prefix}/projects`, params);
  }

  async listProjects(): Promise<Project[]> {
    return this.http.get(`${this.prefix}/projects`);
  }

  /**
   * Read one project with its progress rollup and every ticket in it, in any
   * state. The response is the `ProjectDetail` envelope, NOT a flat `Project`.
   *
   * @param projectId - The project to read.
   * @returns The project, its progress rollup, and its tickets.
   */
  async getProject(projectId: string): Promise<ProjectDetail> {
    return this.http.get(`${this.prefix}/projects/${projectId}`);
  }

  async updateProject(projectId: string, params: UpdateProjectRequest): Promise<Project> {
    return this.http.put(`${this.prefix}/projects/${projectId}`, params);
  }

  async deleteProject(projectId: string): Promise<void> {
    return this.http.delete(`${this.prefix}/projects/${projectId}`);
  }

  /**
   * Assign a project to the org chart position that will own it. Emits
   * `wm.project.assigned`, which is what starts the assignee's work session.
   *
   * @param projectId - The project to assign.
   * @param params - The assignee position id.
   * @returns The project with its new assignee.
   */
  async assignProject(projectId: string, params: AssignProjectRequest): Promise<Project> {
    return this.http.post(`${this.prefix}/projects/${projectId}/assign`, params);
  }

  /**
   * Record the manager's verification decision on a project. `awaiting` asks
   * the manager to verify, `accepted` completes the project, and `not_ready`
   * returns it to its assignee.
   *
   * @param projectId - The project being verified.
   * @param params - The goal the project rolls up to and the verification state.
   * @returns The project with its new verification state.
   */
  async setProjectVerification(
    projectId: string,
    params: SetProjectVerificationRequest
  ): Promise<Project> {
    return this.http.post(`${this.prefix}/projects/${projectId}/verify`, params);
  }

  // --- Initiatives ---

  async createInitiative(params: CreateInitiativeRequest): Promise<Initiative> {
    return this.http.post(`${this.prefix}/initiatives`, params);
  }

  async listInitiatives(): Promise<Initiative[]> {
    return this.http.get(`${this.prefix}/initiatives`);
  }

  async getInitiative(initiativeId: string): Promise<Initiative> {
    return this.http.get(`${this.prefix}/initiatives/${initiativeId}`);
  }

  async updateInitiative(
    initiativeId: string,
    params: UpdateInitiativeRequest
  ): Promise<Initiative> {
    return this.http.put(`${this.prefix}/initiatives/${initiativeId}`, params);
  }

  async deleteInitiative(initiativeId: string): Promise<void> {
    return this.http.delete(`${this.prefix}/initiatives/${initiativeId}`);
  }

  /**
   * Move a project to another initiative of the same goal. A project always
   * belongs to exactly one initiative, so linking re-parents it: afterwards
   * `listInitiativeProjects` on the target includes the project and its
   * previous initiative no longer does. Moving to an initiative of a different
   * goal is refused with a 409 `HIERARCHY_CONFLICT`.
   *
   * @param initiativeId - The initiative the project moves to.
   * @param params - The project to move.
   * @returns `{ linked: true }` once the project belongs to the initiative.
   */
  async linkProjectToInitiative(
    initiativeId: string,
    params: LinkProjectRequest
  ): Promise<unknown> {
    return this.http.post(`${this.prefix}/initiatives/${initiativeId}/projects`, params);
  }

  async listInitiativeProjects(initiativeId: string): Promise<Project[]> {
    return this.http.get(`${this.prefix}/initiatives/${initiativeId}/projects`);
  }

  /**
   * Always refused with a 409 `HIERARCHY_CONFLICT`: a project must belong to
   * an initiative, so it cannot be detached from one. Move it to another
   * initiative of its goal with `linkProjectToInitiative`, or delete it with
   * `deleteProject`.
   *
   * @param initiativeId - The initiative the project belongs to.
   * @param projectId - The project.
   */
  async unlinkProjectFromInitiative(initiativeId: string, projectId: string): Promise<void> {
    return this.http.delete(`${this.prefix}/initiatives/${initiativeId}/projects/${projectId}`);
  }

  // --- Views ---

  async getBoardView(teamId: string): Promise<BoardViewResponse> {
    return this.http.get(`${this.prefix}/views/board/${teamId}`);
  }

  async getAgentView(personaId: string): Promise<unknown> {
    return this.http.get(`${this.prefix}/views/agent/${personaId}`);
  }

  async getMyWorkView(): Promise<unknown> {
    return this.http.get(`${this.prefix}/views/my-work`);
  }

  // --- Session Lookups ---

  async getTicketBySession(sessionId: string): Promise<SessionTicketMapping> {
    return this.http.get(`${this.prefix}/ticket-sessions/by-session/${sessionId}`);
  }

  /**
   * Resolve a batch of session ids to the work items they belong to.
   *
   * Covers all three linked kinds in ONE call — `task` (a ticket), `project`,
   * and `goal` — so a session-list badge does not need a second, goal-shaped
   * lookup beside this one. A session with no linked work item is simply absent
   * from the result; order and length do not mirror the input.
   *
   * Each entry carries the kind, the work item id, a display `identifier` (the
   * ticket identifier, the project name, or the goal objective), the owning
   * `team_id`, and the goal's manager session id when the item rolls up to a
   * goal that has one.
   */
  async getSessionWorkItems(sessionIds: string[]): Promise<SessionWorkItem[]> {
    return this.http.post(`${this.prefix}/work-item-sessions/by-sessions`, {
      session_ids: sessionIds,
    });
  }

  // --- File Provenance ---

  /**
   * List the files a work item changed, with how much changed in each.
   *
   * The list is DERIVED from the work item's sessions: every successful
   * mutating file-tool call, joined to its result on the call id. A turn that
   * wrote two files yields two rows; a failed write and an interrupted call are
   * both absent. Scope is files written with the file tools — a file written by
   * a shell command is not listed, and the surface should say so.
   *
   * Counts come from the stored workspace content, read without a running
   * workspace, so this answers long after the workspace has gone to sleep.
   *
   * @param workItemKind `ticket`, `project` or `goal`
   * @param workItemId The work item's id
   */
  async getWorkItemFiles(workItemKind: string, workItemId: string): Promise<WorkItemFilesResponse> {
    return this.http.get(`${this.prefix}/work-items/${workItemKind}/${workItemId}/files`);
  }

  /**
   * Read one file as it stood at two versions, for a read-only comparison.
   *
   * Returns the two documents and the integer counts — never any rendered
   * text — so the client draws the comparison itself. A side past the per-side
   * size ceiling, or one that is not text, comes back `unavailable` with a typed
   * reason and no content: open the file normally instead of rendering part of
   * it.
   *
   * @param workspaceId The workspace the file lives in
   * @param params The path and the two version ids to compare
   */
  async compareFileVersions(
    workspaceId: string,
    params: CompareFileVersionsParams
  ): Promise<FileComparisonResponse> {
    const query = new URLSearchParams({
      path: params.path,
      from_version: params.from_version,
      to_version: params.to_version,
    });
    return this.http.get(
      `${this.prefix}/workspaces/${workspaceId}/file-comparison?${query.toString()}`
    );
  }

  /**
   * The reverse lookup: which work items wrote this file, newest first.
   *
   * More than one entry is ordinary — a draft written by one work item and
   * revised by another is two entries for the same path.
   *
   * @param workspaceId The workspace the file lives in
   * @param params The path, and an optional cap on how many entries to return
   */
  async listPathWorkItems(
    workspaceId: string,
    params: ListPathWorkItemsParams
  ): Promise<WorkItemPathEntry[]> {
    const query = new URLSearchParams({ path: params.path });
    if (params.limit !== undefined) {
      query.set('limit', String(params.limit));
    }
    return this.http.get(
      `${this.prefix}/workspaces/${workspaceId}/file-work-items?${query.toString()}`
    );
  }

  /**
   * Start rebuilding the reverse lookup for every work item in the account that
   * owns a session, by replaying the same derivation the forward list uses.
   *
   * This does NOT wait for the rebuild to finish. It answers as soon as the
   * rebuild is under way, reporting `state: 'running'` and the counts reached so
   * far; poll by calling it again with the returned `rebuild_id` until `state`
   * is `completed`, and only then are the counts final. There is no cap on how
   * much a rebuild covers — a large account takes longer, it is not cut short.
   *
   * One rebuild runs per account at a time. Calling it with no `rebuild_id`
   * while one is under way reports that one instead of starting a second, and
   * `started` says which of the two happened. Polling a rebuild that is no
   * longer the account's current one fails rather than answering about another.
   *
   * Idempotent: it sets each row rather than adding to it, so running it twice
   * leaves the same rows. Use it to cover work that was done before the reverse
   * lookup existed.
   *
   * @param params Optionally, the rebuild to report on instead of starting one
   */
  async rebuildFileProvenance(
    params?: RebuildFileProvenanceParams
  ): Promise<RebuildFileProvenanceResult> {
    return this.http.post(`${this.prefix}/file-provenance/rebuild`, {
      rebuild_id: params?.rebuild_id,
    });
  }

  async getExecutionCadence(): Promise<ExecutionCadence> {
    return this.http.get(`${this.prefix}/execution-cadence`);
  }

  async setExecutionCadence(params: ExecutionCadence): Promise<ExecutionCadence> {
    return this.http.put(`${this.prefix}/execution-cadence`, params);
  }
}
